export type TokenKind = "comment" | "string" | "number" | "keyword" | "type" | "fn" | "tag" | "punct" | "plain";

export type Token = {
  kind: TokenKind;
  text: string;
};

const KEYWORDS = new Set([
  "import",
  "from",
  "export",
  "default",
  "const",
  "let",
  "var",
  "function",
  "return",
  "type",
  "interface",
  "extends",
  "implements",
  "as",
  "if",
  "else",
  "for",
  "while",
  "of",
  "in",
  "new",
  "true",
  "false",
  "null",
  "undefined",
  "typeof",
  "instanceof",
  "await",
  "async",
  "void",
  "this",
  "throw",
  "try",
  "catch",
  "finally",
  "switch",
  "case",
  "break",
  "continue",
  "satisfies",
  "readonly",
  "enum",
  "declare",
  "public",
  "private",
  "static",
]);

const IDENT_START = /[A-Za-z_$]/;
const IDENT_PART = /[A-Za-z0-9_$]/;

/** 轻量 TS/TSX 词法着色，按行返回 token，足够阅读且不引入额外依赖。 */
export function highlightCode(code: string): Token[][] {
  const lines = code.split("\n");
  const result: Token[][] = [];
  let inBlockComment = false;

  for (const line of lines) {
    const tokens: Token[] = [];
    let index = 0;
    let expectTag = false;

    const push = (kind: TokenKind, text: string) => {
      if (!text) return;
      tokens.push({ kind, text });
    };

    while (index < line.length) {
      if (inBlockComment) {
        const end = line.indexOf("*/", index);
        if (end < 0) {
          push("comment", line.slice(index));
          index = line.length;
          break;
        }
        push("comment", line.slice(index, end + 2));
        index = end + 2;
        inBlockComment = false;
        continue;
      }

      const char = line[index];
      const next = line[index + 1];

      if (char === "/" && next === "/") {
        push("comment", line.slice(index));
        break;
      }

      if (char === "/" && next === "*") {
        const end = line.indexOf("*/", index + 2);
        if (end < 0) {
          push("comment", line.slice(index));
          inBlockComment = true;
          index = line.length;
          break;
        }
        push("comment", line.slice(index, end + 2));
        index = end + 2;
        continue;
      }

      if (char === '"' || char === "'" || char === "`") {
        let end = index + 1;
        while (end < line.length) {
          if (line[end] === "\\") {
            end += 2;
            continue;
          }
          if (line[end] === char) {
            end += 1;
            break;
          }
          end += 1;
        }
        push("string", line.slice(index, end));
        expectTag = false;
        index = end;
        continue;
      }

      if (/[0-9]/.test(char)) {
        let end = index;
        while (end < line.length && /[0-9._a-fA-FxX]/.test(line[end])) end += 1;
        push("number", line.slice(index, end));
        expectTag = false;
        index = end;
        continue;
      }

      if (IDENT_START.test(char)) {
        let end = index + 1;
        while (end < line.length && IDENT_PART.test(line[end])) end += 1;
        const word = line.slice(index, end);
        const after = line.slice(end);
        const isCall = /^\s*\(/.test(after);

        let kind: TokenKind = "plain";
        if (expectTag) kind = "tag";
        else if (KEYWORDS.has(word)) kind = "keyword";
        else if (isCall) kind = "fn";
        else if (/^[A-Z]/.test(word)) kind = "type";

        push(kind, word);
        expectTag = false;
        index = end;
        continue;
      }

      if (char === "<") {
        expectTag = true;
        push("punct", char);
        index += 1;
        continue;
      }

      if (char === "/" && expectTag) {
        push("punct", char);
        index += 1;
        continue;
      }

      if ("()[].,;:=+-*/&|!?><".includes(char)) {
        push("punct", char);
        expectTag = false;
        index += 1;
        continue;
      }

      push("plain", char);
      if (!/\s/.test(char)) expectTag = false;
      index += 1;
    }

    result.push(tokens);
  }

  return result;
}
